/**
 * PVPreluareGenerator - ANEXA 1: PV Predare-Primire Amplasament
 * Formular editabil cu date din contract
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, MapPin } from "lucide-react";

function generatePVPreluareHTML(d, header) {
  const annexLabel = header?.annex_label || "ANEXA";
  const ctrNo = d.contract_number || "___";
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px;color:#1e293b;line-height:1.7;font-size:13px}
    h1{text-align:center;color:#059669;font-size:18px;margin-bottom:4px}
    h2{text-align:center;color:#0f766e;font-size:14px;font-weight:normal;margin-top:0}
    .header{border-bottom:3px solid #059669;padding-bottom:15px;margin-bottom:20px}
    .section{margin-bottom:16px}
    .section-title{font-weight:bold;color:#059669;border-bottom:1px solid #d1fae5;padding-bottom:4px;margin-bottom:8px}
    table{width:100%;border-collapse:collapse;margin:10px 0}
    td{padding:6px 10px;border:1px solid #d1d5db;vertical-align:top}
    td:first-child{width:35%;background:#f0fdf4;font-weight:600;color:#065f46}
    .signatures{display:flex;justify-content:space-between;margin-top:40px;page-break-inside:avoid}
    .sig-box{width:45%;text-align:center;border-top:2px solid #059669;padding-top:10px}
    .sig-box p{margin:4px 0;font-size:12px}
    .footer{text-align:center;margin-top:30px;font-size:11px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:10px}
  </style></head><body>
  <div class="header">
    <h1>PROCES VERBAL DE PREDARE-PRIMIRE AMPLASAMENT</h1>
    <h2>${annexLabel} CTR ${ctrNo} — Preluare Amplasament (Contract nr. ${ctrNo} din ${d.contract_date || "___"})</h2>
  </div>
  <div class="section">
    <div class="section-title">1. Părțile</div>
    <table>
      <tr><td>Executant</td><td>${d.executant_name || "___"}, CUI: ${d.executant_cui || "___"}, ${d.executant_address || "___"}</td></tr>
      <tr><td>Reprezentant executant</td><td>${d.executant_reprezentant || "___"}, ${d.executant_functie || "___"}</td></tr>
      <tr><td>Beneficiar</td><td>${d.beneficiar_name || "___"}, CUI: ${d.beneficiar_cui || "___"}, ${d.beneficiar_address || "___"}</td></tr>
      <tr><td>Reprezentant beneficiar</td><td>${d.beneficiar_reprezentant || "___"}, ${d.beneficiar_functie || "___"}</td></tr>
    </table>
  </div>
  <div class="section">
    <div class="section-title">2. Obiectul</div>
    <table>
      <tr><td>Obiect lucrare</td><td>${d.obiect_lucrare || "___"}</td></tr>
      <tr><td>Locație</td><td>${d.locatie_lucrare || "___"}</td></tr>
      <tr><td>Data predării</td><td>${d.data_predare || "___"}</td></tr>
    </table>
  </div>
  <div class="section">
    <div class="section-title">3. Constatări la predare</div>
    <p>${d.constatari || "Amplasamentul a fost predat și primit în stare corespunzătoare pentru începerea lucrărilor conform contractului."}</p>
  </div>
  <div class="section">
    <div class="section-title">4. Observații</div>
    <p>${d.observatii || "Nu sunt observații."}</p>
  </div>
  <div class="section">
    <div class="section-title">5. Concluzii</div>
    <p>Amplasamentul a fost predat de către <b>${d.beneficiar_name || "___"}</b> și primit de către <b>${d.executant_name || "___"}</b> în vederea executării lucrărilor de <b>${d.obiect_lucrare || "___"}</b>.</p>
  </div>
  <div class="signatures">
    <div class="sig-box">
      <p><strong>PREDAT</strong></p>
      <p>${d.beneficiar_name || "___"}</p>
      <p>${d.beneficiar_reprezentant || "___"}</p>
      <p style="margin-top:30px">(semnătura)</p>
    </div>
    <div class="sig-box">
      <p><strong>PRIMIT</strong></p>
      <p>${d.executant_name || "___"}</p>
      <p>${d.executant_reprezentant || "___"}</p>
      <p style="margin-top:30px">(semnătura)</p>
    </div>
  </div>
  <div class="footer">Document generat de <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a> — ${new Date().toLocaleDateString("ro-RO")}</div>
  </body></html>`;
}

export default function PVPreluareGenerator({ contractData, existingAnnex, onSave, onCancel }) {
  const existing = existingAnnex?.data || {};

  const [form, setForm] = useState({
    contract_number: existing.contract_number || contractData.contract_number || "",
    contract_date: existing.contract_date || contractData.contract_date || "",
    executant_name: existing.executant_name || contractData.executant_name || "",
    executant_cui: existing.executant_cui || contractData.executant_cui || "",
    executant_address: existing.executant_address || contractData.executant_address || "",
    executant_reprezentant: existing.executant_reprezentant || contractData.executant_reprezentant || "",
    executant_functie: existing.executant_functie || contractData.executant_functie || "",
    beneficiar_name: existing.beneficiar_name || contractData.beneficiar_name || "",
    beneficiar_cui: existing.beneficiar_cui || contractData.beneficiar_cui || "",
    beneficiar_address: existing.beneficiar_address || contractData.beneficiar_address || "",
    beneficiar_reprezentant: existing.beneficiar_reprezentant || contractData.beneficiar_reprezentant || "",
    beneficiar_functie: existing.beneficiar_functie || contractData.beneficiar_functie || "",
    obiect_lucrare: existing.obiect_lucrare || contractData.obiect_lucrare || "",
    locatie_lucrare: existing.locatie_lucrare || contractData.locatie_lucrare || "",
    data_predare: existing.data_predare || contractData.contract_start_date || "",
    constatari: existing.constatari || "Amplasamentul a fost predat și primit în stare corespunzătoare pentru începerea lucrărilor conform contractului.",
    observatii: existing.observatii || "",
  });

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = () => {
    const header = { annex_label: existingAnnex?.annex_label || "ANEXA" };
    const html = generatePVPreluareHTML(form, header);
    onSave({
      annex_type: "pv_preluare",
      data: form,
      html_content: html,
      title: "Preluare Amplasament",
    });
  };

  return (
    <div className="space-y-4" data-testid="pv-preluare-editor">
      <Card className="border-0 shadow-lg border-t-4 border-t-emerald-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel} className="p-1 h-auto"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-600" />
                {existingAnnex?.annex_label || "ANEXA"} — PV Predare-Primire Amplasament
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Părți */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-emerald-50 rounded-lg">
            <h3 className="col-span-2 font-semibold text-emerald-800">Executant</h3>
            <div><Label>Nume firmă</Label><Input value={form.executant_name} onChange={(e) => upd("executant_name", e.target.value)} /></div>
            <div><Label>CUI</Label><Input value={form.executant_cui} onChange={(e) => upd("executant_cui", e.target.value)} /></div>
            <div className="col-span-2"><Label>Adresă</Label><Input value={form.executant_address} onChange={(e) => upd("executant_address", e.target.value)} /></div>
            <div><Label>Reprezentant</Label><Input value={form.executant_reprezentant} onChange={(e) => upd("executant_reprezentant", e.target.value)} /></div>
            <div><Label>Funcție</Label><Input value={form.executant_functie} onChange={(e) => upd("executant_functie", e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <h3 className="col-span-2 font-semibold text-slate-800">Beneficiar</h3>
            <div><Label>Nume firmă</Label><Input value={form.beneficiar_name} onChange={(e) => upd("beneficiar_name", e.target.value)} /></div>
            <div><Label>CUI</Label><Input value={form.beneficiar_cui} onChange={(e) => upd("beneficiar_cui", e.target.value)} /></div>
            <div className="col-span-2"><Label>Adresă</Label><Input value={form.beneficiar_address} onChange={(e) => upd("beneficiar_address", e.target.value)} /></div>
            <div><Label>Reprezentant</Label><Input value={form.beneficiar_reprezentant} onChange={(e) => upd("beneficiar_reprezentant", e.target.value)} /></div>
            <div><Label>Funcție</Label><Input value={form.beneficiar_functie} onChange={(e) => upd("beneficiar_functie", e.target.value)} /></div>
          </div>

          {/* Obiect */}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nr. contract</Label><Input value={form.contract_number} onChange={(e) => upd("contract_number", e.target.value)} /></div>
            <div><Label>Data contract</Label><Input type="date" value={form.contract_date} onChange={(e) => upd("contract_date", e.target.value)} /></div>
            <div className="col-span-2"><Label>Obiect lucrare</Label><Input value={form.obiect_lucrare} onChange={(e) => upd("obiect_lucrare", e.target.value)} /></div>
            <div><Label>Locație lucrare</Label><Input value={form.locatie_lucrare} onChange={(e) => upd("locatie_lucrare", e.target.value)} /></div>
            <div><Label>Data predării amplasament</Label><Input type="date" value={form.data_predare} onChange={(e) => upd("data_predare", e.target.value)} /></div>
          </div>

          {/* Constatări */}
          <div>
            <Label>Constatări la predare</Label>
            <Textarea value={form.constatari} onChange={(e) => upd("constatari", e.target.value)} rows={3} />
          </div>
          <div>
            <Label>Observații</Label>
            <Textarea value={form.observatii} onChange={(e) => upd("observatii", e.target.value)} rows={2} placeholder="Observații suplimentare..." />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Anulează</Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-pv-preluare">
              <Save className="w-4 h-4 mr-2" />Salvează {existingAnnex?.annex_label || "ANEXA"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
